import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

interface HelpDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function HelpDialog({
    open,
    onOpenChange,
}: HelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Interfaces
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6 text-sm text-gray-700 leading-relaxed">

                    {/* INTERFACE REPOSITORY */}

                    <h2 className="text-lg font-semibold text-black">
                        Interface Repository
                    </h2>

                    <section className="space-y-3">
                        <p>
                            The Interface Repository is the central location where all
                            interfaces are created, stored, and managed.
                        </p>

                        <p>
                            Users can create and manage two types of interfaces:
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>Internal Interfaces</li>
                            <li>External Interfaces</li>
                        </ul>
                    </section>

                    {/* ADD INTERFACE */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Add Interface
                        </h3>

                        <p>
                            To create a new interface, users must select the
                            interface type, complete all required information,
                            and click the <span className="font-medium">Add</span>{" "}
                            button.
                        </p>

                        <ol className="space-y-2 ml-1">
                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 1 –
                                </span>
                                <span>
                                    Select the interface type (
                                    <span className="font-medium">
                                        Internal
                                    </span>{" "}
                                    or{" "}
                                    <span className="font-medium">
                                        External
                                    </span>
                                    ).
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 2 –
                                </span>
                                <span>
                                    Fill in all required details in the
                                    interface form.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 3 –
                                </span>
                                <span>
                                    Click the{" "}
                                    <span className="font-medium">
                                        Add
                                    </span>{" "}
                                    button.
                                </span>
                            </li>

                            <li className="flex gap-2">
                                <span className="font-semibold shrink-0">
                                    Step 4 –
                                </span>
                                <span>
                                    The interface is stored in the repository
                                    and displayed under its respective category.
                                </span>
                            </li>
                        </ol>
                    </section>

                    {/* INTERNAL INTERFACES */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Internal Interfaces
                        </h3>

                        <h4 className="font-semibold text-black">
                            Add Internal Interface
                        </h4>

                        <p>
                            When users click the{" "}
                            <span className="font-medium">
                                Add Interface
                            </span>{" "}
                            button within Internal Interfaces, a list of all
                            previously added internal interfaces is displayed.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>
                                Users can select required interfaces using
                                checkboxes.
                            </li>

                            <li>
                                Selected interfaces can be associated with the
                                current configuration.
                            </li>

                            <li>
                                Users can remove interfaces from the Internal
                                Interfaces list when required.
                            </li>

                            <li>
                                Removing an interface only removes it from the
                                current selection or view and does not
                                permanently delete it from the repository.
                            </li>
                        </ul>
                    </section>

                    {/* EXTERNAL INTERFACES */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            External Interfaces
                        </h3>

                        <h4 className="font-semibold text-black">
                            Add External Interface
                        </h4>

                        <p>
                            When users click the{" "}
                            <span className="font-medium">
                                Add Interface
                            </span>{" "}
                            button within External Interfaces, a list of all
                            previously added external interfaces is displayed.
                        </p>

                        <ul className="list-disc ml-5 space-y-1">
                            <li>
                                Users can select required interfaces using
                                checkboxes.
                            </li>

                            <li>
                                Selected interfaces can be associated with the
                                current configuration.
                            </li>

                            <li>
                                Users can remove interfaces from the External
                                Interfaces list when required.
                            </li>

                            <li>
                                Removing an interface only removes it from the
                                current selection or view and does not
                                permanently delete it from the repository.
                            </li>
                        </ul>
                    </section>

                    {/* FILTER INTERFACES */}

                    <section className="space-y-3">
                        <h3 className="text-base font-bold text-black">
                            Filter Interfaces
                        </h3>

                        <p>
                            Users can easily filter interfaces in the repository
                            to quickly locate specific records, especially when
                            the repository contains a large number of entries.
                        </p>

                        <div>
                            <p className="font-semibold text-black">
                                Available Filters
                            </p>

                            <ul className="list-disc ml-5 mt-1 space-y-1">
                                <li>
                                    <span className="font-medium">
                                        Interface ID
                                    </span>{" "}
                                    – Find a specific interface directly using
                                    its unique identifier.
                                </li>

                                <li>
                                    <span className="font-medium">
                                        Type
                                    </span>{" "}
                                    – Filter interfaces by Internal or External
                                    type.
                                </li>

                                <li>
                                    <span className="font-medium">
                                        Name
                                    </span>{" "}
                                    – Search for interfaces using the interface
                                    name.
                                </li>
                            </ul>
                        </div>

                        <p>
                            These filtering options help users efficiently
                            navigate and manage interfaces within the repository.
                        </p>
                    </section>

                </div>
            </DialogContent>
        </Dialog>
    );
}